import _ from 'lodash';

import { bindCallbacks, statusText } from 'scripts/infrastructure/backends/fake_backend_http/lib/common';
import ConversationItemsService from 'scripts/infrastructure/backends/fake_backend/conversation_items_service';
import IdGenerator from 'scripts/domain/contracts/id_generator';

// Task-scoped equivalents of the conversation topic/custom-attribute writes in
// conversation_history_service. Both mirror the two-publish shape that
// conversations_service uses for the conversation-level endpoints: publish the
// mutated parent (here the task conversation item) and then append the
// synthesized audit item to the timeline.
//
// The routes carry only :taskId, so the owning customer is resolved by scanning
// the org's conversation histories.
export default class TaskTopicsService {
  constructor(database, pubsub) {
    this.getDatabase = database;
    this.pubsub = pubsub;
    this.conversationItems = ConversationItemsService.create(pubsub, database);
  }

  // POST body is the shared conversationtopic.AssociationUpdate shape:
  //   { associations: [{ topicId }], removeTopicIds: [id, ...] }
  updateTopicAssociations(attrs, callback, path, { orgId, taskId }) {
    const found = this._findTask(orgId, taskId);
    if (!found) {
      return callback(Error(statusText(404)), { status: 404, statusText: statusText(404) });
    }
    const { customerId, taskItem } = found;

    const added = _.map(attrs.associations || [], 'topicId');
    const removed = attrs.removeTopicIds || [];

    taskItem.content.topicIds = _.difference(_.union(taskItem.content.topicIds || [], added), removed);
    this.conversationItems.update(orgId, customerId, taskId, { correlationId: null, payload: {} });

    this._addAuditItem(orgId, customerId, taskItem, {
      addedTopics: added,
      removedTopics: removed,
      taskId,
      type: 'TASK_TOPIC_CHANGE',
    });

    callback(null, { status: 204, statusText: statusText(204) });
  }

  // POST body is the shared CustomAttributeUpdate shape:
  //   { add: [{ key, value }], remove: [{ key, value }] }
  // Values are keyed by `key` on the wire and by `id` on the task, so translate
  // against the org registry the same way conversations_service does.
  updateCustomAttributes(attrs, callback, path, { orgId, taskId }) {
    const found = this._findTask(orgId, taskId);
    if (!found) {
      return callback(Error(statusText(404)), { status: 404, statusText: statusText(404) });
    }
    const { customerId, taskItem } = found;

    const added = this._toIdValues(orgId, attrs.add || []);
    const removed = this._toIdValues(orgId, attrs.remove || []);

    if (_.some(added, attr => attr.value && attr.value.length > 255)) {
      return callback({ errors: [{ code: 'too_long' }] }, { status: 400, statusText: statusText(400) });
    }

    const equalAttrs = (a, b) => a.id === b.id && a.value === b.value;
    const customAttributes = _.unionWith(taskItem.content.customAttributes || [], added, equalAttrs);
    taskItem.content.customAttributes = _.differenceWith(customAttributes, removed, equalAttrs);
    this.conversationItems.update(orgId, customerId, taskId, { correlationId: null, payload: {} });

    this._addAuditItem(orgId, customerId, taskItem, {
      added,
      removed,
      taskId,
      type: 'TASK_CUSTOM_ATTRIBUTE_CHANGE',
    });

    callback(null, { status: 204, statusText: statusText(204) });
  }

  _addAuditItem(orgId, customerId, taskItem, content) {
    this.conversationItems.add(orgId, customerId, {
      correlationId: null,
      payload: {
        id: IdGenerator.newId(),
        content,
        conversationId: taskItem.conversationId,
        customerId,
        initiator: taskItem.initiator,
        timestamp: new Date().toISOString(),
        version: 0,
      },
    });
  }

  _toIdValues(orgId, keyValues) {
    const definitions = this.getDatabase(orgId).customAttributes || [];
    const idsByKey = _.reduce(
      definitions,
      (acc, attr) => {
        acc[attr.key] = attr.id;
        return acc;
      },
      {}
    );
    return keyValues.map(attr => ({ id: idsByKey[attr.key], value: attr.value }));
  }

  _findTask(orgId, taskId) {
    const customers = this.getDatabase(orgId).customers || [];
    for (const customer of customers) {
      const taskItem = _.find(customer.conversationHistory, { id: taskId });
      if (taskItem) {
        return { customerId: customer.id, taskItem };
      }
    }
    return null;
  }

  getRoutes() {
    return bindCallbacks(
      {
        '/api/v1/orgs/:orgId/tasks/:taskId/topic-associations': {
          POST: this.updateTopicAssociations,
        },
        '/api/v1/orgs/:orgId/tasks/:taskId/custom-attributes': {
          POST: this.updateCustomAttributes,
        },
      },
      this
    );
  }
}
